import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  List, ListItemAvatar, ListItemButton, ListItemText, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { EmptyState, Loader, MemberAvatar, PageHeader, SectionTitle, useHousehold } from '../common';
import { portionText, shortDateTime, time, toTimeOnly } from '../labels';

/** "Adulto · 1 dose · tu", "Criança · ½ dose · sem conta", "Adulto · 1 dose · convite enviado a ana@…". */
const memberSubtitle = (m: T.HouseholdMember) => {
  const portion = portionText(m.portionWeight);
  if (m.hasAccount) return `${portion} · ${m.isCurrentUser ? 'tu' : m.email ?? ''}`;
  if (m.pendingInvitation) return `${portion} · convite enviado a ${m.pendingInvitation.email}`;
  return `${portion} · sem conta`;
};

/** Invitations to join another household, shown while the user has none. */
function ReceivedInvitations() {
  const { set } = useHousehold();
  const [invitations, setInvitations] = useState<T.ReceivedInvitation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getInvitations().then(setInvitations).catch(e => setError(e.message));
  }, []);

  const answer = async (invitation: T.ReceivedInvitation, accept: boolean) => {
    setError(null);
    setBusy(true);
    try {
      if (accept) {
        set(await api.acceptInvitation(invitation.publicId));
      } else {
        await api.declineInvitation(invitation.publicId);
        setInvitations(list => list.filter(i => i.publicId !== invitation.publicId));
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (invitations.length === 0 && !error) return null;
  return (
    <Stack spacing={1.5} sx={{ maxWidth: 520, mb: 2 }}>
      {invitations.map(i => (
        <Card key={i.publicId} variant="outlined">
          <CardContent>
            <Typography variant="h6">Convite para {i.householdName}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {i.invitedBy ? `${i.invitedBy} convidou-te` : 'Foste convidado'} para entrares na casa como {i.memberName}. Válido até {shortDateTime(i.expiresAt)}.
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={() => answer(i, true)} disabled={busy}>Aceitar</Button>
              <Button onClick={() => answer(i, false)} disabled={busy}>Recusar</Button>
            </Stack>
          </CardContent>
        </Card>
      ))}
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
}

/** First run: the household does not exist yet. */
function CreateHousehold() {
  const { set } = useHousehold();
  const [name, setName] = useState('');
  const [memberName, setMemberName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!name.trim()) { setError('Dá um nome à casa.'); return; }
    setError(null);
    setBusy(true);
    try {
      set(await api.createHousehold({ name: name.trim(), memberName: memberName.trim() || null }));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ maxWidth: 520 }}>
      <CardContent>
        <Typography variant="h6">Cria a tua casa</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          As ementas, a lista de compras e as tarefas são partilhadas por todas as pessoas da casa.
        </Typography>
        <Stack spacing={2} component="form" onSubmit={e => { e.preventDefault(); create(); }}>
          <TextField label="Nome da casa" placeholder="Ex.: Casa Costa" value={name} onChange={e => setName(e.target.value)} autoFocus />
          <TextField label="O teu nome" placeholder="Opcional — usa o nome da conta" value={memberName} onChange={e => setMemberName(e.target.value)} />
          {error && <Alert severity="error">{error}</Alert>}
          <Box><Button type="submit" variant="contained" disabled={busy}>Criar</Button></Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

/** Meal prep tasks are due relative to these times. */
function MealTimesDialog({ household, open, onClose }: { household: T.Household; open: boolean; onClose: () => void }) {
  const { set } = useHousehold();
  const [lunch, setLunch] = useState(time(household.lunchTime));
  const [dinner, setDinner] = useState(time(household.dinnerTime));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLunch(time(household.lunchTime));
    setDinner(time(household.dinnerTime));
    setError(null);
  }, [open, household]);

  const save = async () => {
    const lunchTime = toTimeOnly(lunch);
    const dinnerTime = toTimeOnly(dinner);
    if (!lunchTime || !dinnerTime) { setError('Hora inválida.'); return; }
    setBusy(true);
    try {
      set(await api.updateMealTimes({ lunchTime, dinnerTime }));
      onClose();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Horas das refeições</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label="Almoço" type="time" value={lunch} onChange={e => setLunch(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField label="Jantar" type="time" value={dinner} onChange={e => setDinner(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={busy}>Guardar</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Household name, meal times and the people living in it. */
const HouseholdPage: React.FC = () => {
  const { household, members, loading, error, reload, set } = useHousehold();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [timesOpen, setTimesOpen] = useState(false);

  useEffect(() => { if (household) setName(household.name); }, [household]);

  const saveName = async () => {
    if (!name.trim()) { setNameError('Dá um nome à casa.'); return; }
    setNameError(null);
    setSaving(true);
    try {
      set(await api.updateHousehold({ name: name.trim() }));
    } catch (e: any) {
      setNameError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <PageHeader
        title="Casa e pessoas"
        actions={household && (
          <>
            <Tooltip title="Atualizar"><IconButton onClick={reload}><RefreshIcon /></IconButton></Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} component={RouterLink} to="pessoa/nova">Pessoa</Button>
          </>
        )}
      />
      <Loader loading={loading} error={error} onRetry={reload} hasData={!loading && !error}>
        {!household ? <><ReceivedInvitations /><CreateHousehold /></> : (
          <>
            <Stack direction="row" spacing={1} component="form" onSubmit={e => { e.preventDefault(); saveName(); }} sx={{ alignItems: 'flex-start' }}>
              <TextField label="Nome da casa" value={name} onChange={e => setName(e.target.value)} size="small" fullWidth />
              <Button type="submit" variant="outlined" disabled={saving || name.trim() === household.name}>Guardar</Button>
            </Stack>
            {nameError && <Alert severity="error" sx={{ mt: 1 }}>{nameError}</Alert>}

            <SectionTitle>Pessoas</SectionTitle>
            <Typography variant="body2" color="text.secondary">
              A dose define quanto conta cada pessoa nas ementas (adulto = 1, criança = ½).
            </Typography>
            <Stack spacing={1} sx={{ my: 1.5, alignItems: 'flex-start' }}>
              <Button variant="outlined" component={RouterLink} to="/casa/refeicoes/padroes">Quem come em cada refeição</Button>
              <Button variant="outlined" onClick={() => setTimesOpen(true)} sx={{ textAlign: 'left' }}>
                Horas das refeições: almoço {time(household.lunchTime)}, jantar {time(household.dinnerTime)}
              </Button>
              <Button variant="outlined" component={RouterLink} to="/casa/refeicoes/regras">Regras das sugestões de ementas</Button>
            </Stack>

            {members.length === 0 ? <EmptyState title="Ainda não há pessoas" /> : (
              <List disablePadding>
                {members.map(m => (
                  <ListItemButton key={m.publicId} onClick={() => navigate(`pessoa/${m.publicId}`)} sx={{ borderRadius: 1 }}>
                    <ListItemAvatar><MemberAvatar member={m} size={40} /></ListItemAvatar>
                    <ListItemText primary={m.name} secondary={memberSubtitle(m)} slotProps={{ primary: { sx: { fontWeight: 600 } } }} />
                  </ListItemButton>
                ))}
              </List>
            )}
            <MealTimesDialog household={household} open={timesOpen} onClose={() => setTimesOpen(false)} />
          </>
        )}
      </Loader>
    </Box>
  );
};

export default HouseholdPage;
